import { BaseApiService } from './baseApiService';

interface PresignedUploadUrlResponse {
  status: string;
  message: string;
  data: {
    recording_id: number;
    upload_url: string;
    audio_file_url: string;
  };
}

interface TranscriptionData {
  transcript: string;
  confidence: number;
  duration: number;
}

interface ConfirmUploadResponse {
  status: string;
  message: string;
  data: {
    recording_id: number;
    status: string;
    audio_file_url: string;
    file_url: string;
    transcription: TranscriptionData;
  };
}

const RECORDING_UPLOAD_MAX_RETRIES = 3;
const RECORDING_UPLOAD_RETRY_DELAY_MS = 1000;

class ConsultRecordingService extends BaseApiService {
  async getPresignedUploadUrl(
    sessionId: number,
    filename: string,
    patientId?: number,
    clientRecordingId?: string,
    signal?: AbortSignal
  ): Promise<PresignedUploadUrlResponse['data']> {
    const body: { session_id: number; filename: string; patient_id?: number; client_recording_id?: string } = {
      session_id: sessionId,
      filename,
    };

    if (clientRecordingId) body.client_recording_id = clientRecordingId;

    if (patientId !== undefined) {
      body.patient_id = patientId;
    }

    const response: PresignedUploadUrlResponse = await this.fetchWithAuth(
      '/recording-upload-url/',
      {
        method: 'POST',
        signal,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      }
    );

    if (response.status !== 'success') {
      throw new Error(response.message || 'Failed to get presigned upload URL');
    }

    return response.data;
  }

  async uploadToS3(uploadUrl: string, audioBlob: Blob, signal?: AbortSignal): Promise<void> {
    // The backend signs the canonical MIME type derived from the filename.
    // MediaRecorder adds codec parameters, which invalidate that signature.
    const contentTypes: Record<string, string> = {
      webm: 'audio/webm', ogg: 'audio/ogg', mp4: 'audio/mp4',
      mp3: 'audio/mpeg', wav: 'audio/wav',
    };
    const contentType = contentTypes[this.getExtensionFromMime(audioBlob.type)] || audioBlob.type.split(';')[0];
    let lastError: Error | undefined;

    for (let attempt = 1; attempt <= RECORDING_UPLOAD_MAX_RETRIES; attempt++) {
      if (signal?.aborted) throw new Error('Upload cancelled after account change.');
      try {
        const response = await fetch(uploadUrl, {
          method: 'PUT',
          signal,
          body: audioBlob,
          headers: {
            'Content-Type': contentType,
          },
        });

        if (!response.ok) {
          throw new Error(`Recording upload failed (${response.status}). Please retry.`);
        }

        return;
      } catch (error) {
        if (signal?.aborted) throw error;
        lastError = error instanceof Error ? error : new Error('Recording upload failed. Please retry.');
        if (attempt < RECORDING_UPLOAD_MAX_RETRIES) {
          await new Promise((resolve) => setTimeout(resolve, RECORDING_UPLOAD_RETRY_DELAY_MS));
        }
      }
    }

    throw lastError ?? new Error('Recording upload failed. Please retry.');
  }

  async confirmUpload(recordingId: number): Promise<ConfirmUploadResponse['data']> {
    const response: ConfirmUploadResponse = await this.fetchWithAuth(
      '/recording-confirm/',
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ recording_id: recordingId }),
      }
    );

    if (response.status !== 'success') {
      throw new Error(response.message || 'Failed to confirm recording upload');
    }

    return response.data;
  }

  getExtensionFromMime(mimeType?: string): string {
    if (!mimeType) return 'webm';
    if (mimeType.includes('wav')) return 'wav';
    if (mimeType.includes('webm')) return 'webm';
    if (mimeType.includes('ogg')) return 'ogg';
    if (mimeType.includes('mp4')) return 'mp4';
    if (mimeType.includes('mpeg')) return 'mp3';
    return 'dat';
  }
}

export const consultRecordingService = new ConsultRecordingService();
